import { Grid } from '@mui/material';
import { ODSText, ODSTagStatic, ODSLoadingSpinner } from '@telekom-ods/react-ui-kit';
import type { ODSTagStaticType } from '@telekom-ods/react-ui-kit';
import { useIntl } from 'react-intl';

import Alert from '../../../../../components/Alert';
import Button from '../../../../../components/Buttons/Button';
import Tooltip from '../../../../../components/Tooltip';

import { parseProgress } from './upgradeProgressHelpers';

import './UpgradeCluster.css';

import type { ClusterUpgradeStatusItem } from '@/types/caasTypes';

interface UpgradeProgressProps {
  statusItem?: ClusterUpgradeStatusItem | undefined;
  isLive: boolean;
  connectionLost?: boolean;
  upgradeRequested?: boolean;
  onRefresh?: () => void;
}

const formatDateTime = (intl: ReturnType<typeof useIntl>, value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  return `${intl.formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' })} · ${intl.formatTime(date, { hour: '2-digit', minute: '2-digit' })}`;
};

type PhaseState = 'ok' | 'run' | 'wait' | 'fail';

const friendlyPostCheck = (intl: ReturnType<typeof useIntl>, value?: string): string => {
  const labels: Record<string, string> = {
    created: intl.formatMessage({ id: 'runningLabel' }),
    pending: intl.formatMessage({ id: 'runningLabel' }),
    running: intl.formatMessage({ id: 'runningLabel' }),
    inprogress: intl.formatMessage({ id: 'runningLabel' }),
    passed: intl.formatMessage({ id: 'passed' }),
    success: intl.formatMessage({ id: 'passed' }),
    failed: intl.formatMessage({ id: 'failed' }),
    error: intl.formatMessage({ id: 'failed' }),
  };
  if (!value) return intl.formatMessage({ id: 'pendingLabel' });
  return labels[value.toLowerCase()] ?? value;
};

const PHASE_GLYPH: Record<PhaseState, string> = { ok: '✓', run: '', fail: '!', wait: '' };
const PHASE_TAG_TYPE: Record<PhaseState, ODSTagStaticType> = {
  ok: 'success',
  fail: 'error',
  run: 'warning',
  wait: 'subtle',
};

interface AccProps {
  title: string;
  status?: React.ReactNode;
  defaultOpen?: boolean;
  small?: boolean;
  children: React.ReactNode;
}
const Accordion = ({
  title,
  status,
  defaultOpen,
  small,
  children,
}: AccProps): React.JSX.Element => (
  <details
    className={`upgrade-acc${small ? ' upgrade-acc-nested' : ''}`}
    {...(defaultOpen ? { open: true } : {})}
  >
    <summary className='upgrade-acc-sum'>
      <span
        className='upgrade-acc-chev'
        aria-hidden='true'
      >
        ›
      </span>
      <span className='upgrade-acc-title'>{title}</span>
      {status !== undefined && <span className='upgrade-acc-status'>{status}</span>}
    </summary>
    <div className='upgrade-acc-body'>{children}</div>
  </details>
);

const InfoLine = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Grid
    container
    alignItems='center'
    justifyContent='space-between'
    className='upgrade-row'
  >
    <ODSText
      as='p'
      className='p-text'
    >
      {label}
    </ODSText>
    {children}
  </Grid>
);

const UpgradeProgress = ({
  statusItem,
  isLive,
  connectionLost,
  upgradeRequested,
  onRefresh,
}: UpgradeProgressProps): React.JSX.Element => {
  const intl = useIntl();
  const hasStatus = !!statusItem;
  const upgradeConfig = statusItem?.spec;
  const upgradeStatus = statusItem?.status;
  const isFinished = upgradeStatus?.finished === true;

  const processing = upgradeStatus?.functions?.processing as Record<string, string> | undefined;
  const processingEntries = processing ? Object.entries(processing) : [];

  const postCheck = upgradeStatus?.postCheck;
  const isPostCheckFailed = postCheck === 'failed' || postCheck === 'error';
  const isPostCheckPassed = postCheck === 'passed' || postCheck === 'success';
  const isPostCheckResolved = isPostCheckFailed || isPostCheckPassed;

  const postCheckLabel = friendlyPostCheck(intl, upgradeStatus?.postCheck);
  const requestor = upgradeConfig?.requestor ?? '—';
  const requestorShort = requestor.split('@')[0] ?? requestor;
  const version = upgradeConfig?.newVersion;

  const functionRows = processingEntries.map(([name, fnStatus]) => ({ name, status: fnStatus }));

  const workers = upgradeStatus?.workers;
  const masterGroups = upgradeStatus?.masters
    ? [{ name: 'master', status: upgradeStatus.masters }]
    : [];
  const workerGroups = workers
    ? Object.entries(workers).map(([name, ws]) => ({ name, status: ws }))
    : [];

  const isGroupDone = (machinesDoneNum?: string): boolean => {
    const p = parseProgress(machinesDoneNum);
    return p ? p.done === p.total : false;
  };
  // Once finished, the CR reports node counts back as 0/N — treat finished as
  // done so the completed view doesn't read as "nothing upgraded". An absent
  // node-group section means there's nothing to roll there, so treat it as done.
  const mastersDone =
    isFinished ||
    masterGroups.length === 0 ||
    masterGroups.every(g => isGroupDone(g.status.machinesDoneNum));
  const workerDoneCount = workerGroups.filter(g => isGroupDone(g.status.machinesDoneNum)).length;
  const workersDone =
    isFinished || workerGroups.length === 0 || workerDoneCount === workerGroups.length;
  const functionsActive = processingEntries.length > 0;
  const functionsDone = isFinished || (!functionsActive && mastersDone && workersDone);

  const phaseDone = [mastersDone, workersDone, functionsDone, isPostCheckPassed];
  const activePhase = phaseDone.findIndex(done => !done);
  const phaseState = (idx: number): PhaseState => {
    if (idx === 3 && isPostCheckFailed) return 'fail';
    if (phaseDone[idx]) return 'ok';
    if (idx === activePhase) return 'run';
    return 'wait';
  };
  const doneLabel = intl.formatMessage({ id: 'doneLabel' });
  const mastersDetail = isFinished
    ? doneLabel
    : (masterGroups[0]?.status.machinesDoneNum ?? String(masterGroups.length));
  const workersDetail = isFinished
    ? doneLabel
    : `${String(workerDoneCount)} / ${String(workerGroups.length)}`;
  let functionsDetail = intl.formatMessage({ id: 'waiting' });
  if (functionsActive) functionsDetail = intl.formatMessage({ id: 'progressingLabel' });
  else if (functionsDone) functionsDetail = intl.formatMessage({ id: 'readyLabel' });
  const phases: { name: string; state: PhaseState; detail: string }[] = [
    {
      name: intl.formatMessage({ id: 'masterNodes' }),
      state: phaseState(0),
      detail: mastersDetail,
    },
    {
      name: intl.formatMessage({ id: 'workerGroups' }),
      state: phaseState(1),
      detail: workersDetail,
    },
    {
      name: intl.formatMessage({ id: 'platformFunctionsPhase' }),
      state: phaseState(2),
      detail: functionsDetail,
    },
    {
      name: intl.formatMessage({ id: 'postUpgradeCheckPhase' }),
      state: phaseState(3),
      detail: postCheckLabel,
    },
  ];

  const isActiveUpgrade = hasStatus && !(isFinished && isPostCheckResolved);

  // Verdict banner (ODSInlineNotification via Alert).
  let banner: { mode: 'error' | 'success' | 'informative'; title: string; text: string } | null =
    null;
  if (isFinished && isPostCheckFailed) {
    banner = {
      mode: 'error',
      title: intl.formatMessage({ id: 'postUpgradeCheckFailedTitle' }),
      text: version
        ? intl.formatMessage({ id: 'postUpgradeCheckFailedTextWithVersion' }, { version })
        : intl.formatMessage({ id: 'postUpgradeCheckFailedTextNoVersion' }),
    };
  } else if (isFinished && isPostCheckPassed) {
    banner = {
      mode: 'success',
      title: intl.formatMessage({ id: 'upgradeCompleteTitle' }),
      text: intl.formatMessage(
        { id: 'upgradeCompleteText' },
        { version: version ?? intl.formatMessage({ id: 'theNewVersionFallback' }) }
      ),
    };
  } else if (isActiveUpgrade) {
    banner = {
      mode: 'informative',
      title: version
        ? intl.formatMessage({ id: 'upgradingTitleWithVersion' }, { version })
        : intl.formatMessage({ id: 'upgradingTitle' }),
      text: intl.formatMessage({ id: 'upgradeInProgressBannerText' }),
    };
  }

  let progressStatus: React.JSX.Element;
  if (isPostCheckFailed) {
    progressStatus = (
      <ODSTagStatic
        label={intl.formatMessage({ id: 'failed' })}
        type='error'
      />
    );
  } else if (isFinished && isPostCheckPassed) {
    progressStatus = (
      <ODSTagStatic
        label={intl.formatMessage({ id: 'completed' })}
        type='success'
      />
    );
  } else if (isLive && !connectionLost) {
    progressStatus = (
      <span className='upgrade-live'>
        <span className='upgrade-pulse' />
        {intl.formatMessage({ id: 'liveLabel' })}
      </span>
    );
  } else {
    progressStatus = <ODSLoadingSpinner size='small' />;
  }

  if (!hasStatus && !upgradeRequested) {
    return (
      <Grid
        container
        direction='column'
        gap={1.5}
        className='upgrade-section-card'
      >
        <ODSText
          as='p'
          className='upgrade-section-title'
        >
          {intl.formatMessage({ id: 'upgradeStatusTitle' })}
        </ODSText>
        <ODSText
          as='p'
          className='p-text upgrade-text-muted'
        >
          {intl.formatMessage({ id: 'noUpgradeRunning' })}
        </ODSText>
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction='column'
      gap={1.5}
    >
      {banner && (
        <Alert
          mode={banner.mode}
          title={banner.title}
          text={banner.text}
        />
      )}

      {hasStatus && connectionLost && (
        <Alert
          mode='warning'
          title={intl.formatMessage({ id: 'statusRefreshFailed' })}
          text={intl.formatMessage({ id: 'statusRefreshFailedText' })}
        />
      )}

      {version && (
        <Accordion
          title={intl.formatMessage({ id: 'upgradeDetails' })}
          status={<span className='upgrade-acc-sub'>{version}</span>}
        >
          <InfoLine label={intl.formatMessage({ id: 'targetVersion' })}>
            <ODSTagStatic
              label={version}
              type='success'
            />
          </InfoLine>
          <InfoLine label={intl.formatMessage({ id: 'requestor' })}>
            <Tooltip label={requestor}>
              <span className='upgrade-requestor'>{requestorShort}</span>
            </Tooltip>
          </InfoLine>
          <InfoLine label={intl.formatMessage({ id: 'triggeredAt' })}>
            <ODSText
              as='p'
              className='upgrade-mval'
            >
              {formatDateTime(intl, upgradeConfig.triggeredAt ?? statusItem?.triggeredAt)}
            </ODSText>
          </InfoLine>
        </Accordion>
      )}

      <Accordion
        title={intl.formatMessage({ id: 'upgradeProgress' })}
        status={progressStatus}
        defaultOpen
      >
        <div className='upgrade-list'>
          {phases.map(phase => (
            <div
              key={phase.name}
              className={`upgrade-prow upgrade-prow-${phase.state}`}
            >
              <span className={`upgrade-ico upgrade-ico-${phase.state}`}>
                {PHASE_GLYPH[phase.state]}
              </span>
              <ODSText
                as='p'
                className='upgrade-prow-label'
              >
                {phase.name}
              </ODSText>
              <ODSTagStatic
                label={phase.detail}
                type={PHASE_TAG_TYPE[phase.state]}
              />
            </div>
          ))}
        </div>

        {(masterGroups.length > 0 || workerGroups.length > 0) && (
          <Accordion
            title={intl.formatMessage({ id: 'nodeWorkerDetailTitle' })}
            small
            status={
              <ODSTagStatic
                label={
                  isFinished
                    ? doneLabel
                    : `${String(workerDoneCount)} / ${String(workerGroups.length)}`
                }
                type={isFinished ? 'success' : 'subtle'}
              />
            }
          >
            {masterGroups.length > 0 && (
              <InfoLine label={intl.formatMessage({ id: 'masterNodes' })}>
                <ODSTagStatic
                  label={mastersDetail}
                  type={mastersDone ? 'success' : 'subtle'}
                />
              </InfoLine>
            )}
            {workerGroups.length > 0 && (
              <>
                <InfoLine label={intl.formatMessage({ id: 'workerGroups' })}>
                  <ODSTagStatic
                    label={workersDetail}
                    type={workersDone ? 'success' : 'subtle'}
                  />
                </InfoLine>
                {workerGroups.map(({ name, status: gs }) => {
                  const p = parseProgress(gs.machinesDoneNum);
                  const done = isFinished || (p ? p.done === p.total : false);
                  let label = '—';
                  if (isFinished) label = doneLabel;
                  else if (gs.machinesDoneNum && gs.machinesDoneNum.length > 0)
                    label = gs.machinesDoneNum;
                  return (
                    <div
                      key={name}
                      className='upgrade-subrow'
                    >
                      <ODSText
                        as='p'
                        className='p-text'
                      >
                        {name}
                      </ODSText>
                      <ODSTagStatic
                        label={label}
                        type={done ? 'success' : 'subtle'}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </Accordion>
        )}

        <Accordion
          title={intl.formatMessage({ id: 'functions' })}
          small
          status={
            <ODSTagStatic
              label={functionsDone ? doneLabel : functionsDetail}
              type={functionsDone ? 'success' : 'subtle'}
            />
          }
        >
          {functionRows.length > 0 ? (
            functionRows.map(fn => (
              <InfoLine
                key={fn.name}
                label={fn.name}
              >
                <ODSTagStatic
                  label={fn.status}
                  type='warning'
                />
              </InfoLine>
            ))
          ) : (
            <ODSText
              as='p'
              className='p-text upgrade-text-muted'
            >
              {intl.formatMessage({
                id: isFinished ? 'allFunctionsCompleted' : 'waitingForPlatformFunctions',
              })}
            </ODSText>
          )}
        </Accordion>

        {onRefresh && (
          <Grid
            container
            justifyContent='flex-end'
            sx={{ marginTop: '8px' }}
          >
            <Button
              variant='outline'
              size='small'
              label={intl.formatMessage({ id: 'refreshNow' })}
              onClick={onRefresh}
            />
          </Grid>
        )}
      </Accordion>
    </Grid>
  );
};

export default UpgradeProgress;
